import { NULL_AS_STRING } from '@repo/shared/dtos/beneficiary'
import { MultiselectSchemaItem } from '@repo/shared/dtos/structured-data'
import { Button } from '@repo/ui/button'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@repo/ui/form'
import { useT } from '@repo/ui/hooks'
import { Input } from '@repo/ui/input'
import { MultiSelect } from '@repo/ui/multiSelect'
import { TooltipWrapper } from '@repo/ui/tooltip'
import { onInput } from '@repo/ui/utils'
import { PlusIcon, Trash2Icon } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { ControllerRenderProps, UseFormReturn } from 'react-hook-form'

import { BaseForm } from '@/components/admin/structure-data/schema/forms/BaseForm'
import {
  MultiselectFormValues,
  multiselectValidationSchema,
  stringArrayNullToString,
  stringToStringArrayNull,
} from '@/components/admin/structure-data/schema/forms/validation'

export function ItemMultiselectForm({
  item,
  onUpdate,
}: {
  item: MultiselectSchemaItem
  onUpdate: (item: MultiselectSchemaItem) => void
}) {
  const defaultValues = useMemo(
    () =>
      ({
        label: item.label,
        instructionsToFillValue: item.instructionsToFillValue,
        defaultValue: stringArrayNullToString(item.defaultValue),
        enumValues: item.enumValues,
        type: 'multiselect',
      }) satisfies MultiselectFormValues,
    [item],
  )

  const handleUpdate = (values: MultiselectFormValues) => {
    onUpdate({
      ...item,
      ...values,
      defaultValue: stringToStringArrayNull(values.defaultValue),
    })
  }

  return (
    <BaseForm
      defaultValues={defaultValues}
      onUpdate={handleUpdate}
      schema={multiselectValidationSchema}
    >
      {renderFields}
    </BaseForm>
  )
}

type FormApi = UseFormReturn<MultiselectFormValues>
function renderFields(formApi: FormApi) {
  const enumValues = formApi.watch('enumValues')
  return (
    <>
      <FormField
        control={formApi.control}
        name="enumValues"
        render={renderEnumValuesField(formApi)}
      />

      <FormField
        control={formApi.control}
        name="defaultValue"
        render={renderDefaultValueField(formApi, enumValues)}
      />
    </>
  )
}

const renderEnumValuesField =
  (formApi: FormApi) =>
  ({ field }: { field: ControllerRenderProps<MultiselectFormValues, 'enumValues'> }) => {
    const { tc, t } = useT({ keyPrefix: 'errors.validation' })
    const enumValues = field.value
    const [newEnumValue, setNewEnumValue] = useState('')
    const handleChange = (values: string[]) => {
      formApi.clearErrors('enumValues')
      formApi.setValue('enumValues', values, {
        shouldDirty: true,
        shouldValidate: true,
      })
    }
    return (
      <FormItem>
        <FormLabel className={enumValues.length === 0 ? 'text-red-700' : ''}>
          {tc('choice', { cfl: true, count: enumValues.length })}
        </FormLabel>
        <FormControl>
          <div className="flex flex-col gap-2">
            {enumValues.map((value, index) => (
              <div key={index} className="flex items-center gap-2">
                <Input
                  id={`enumValues.${index}`}
                  placeholder={tc('choice', { cfl: true })}
                  value={value}
                  onChange={onInput((value) => {
                    if (value === NULL_AS_STRING) return
                    const newEnumValues = enumValues.map((v, i) => (i === index ? value : v))
                    handleChange(newEnumValues)
                  })}
                />
                <TooltipWrapper content={tc('delete', { cfl: true })}>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      const newValues = [...enumValues]
                      newValues.splice(index, 1)
                      handleChange(newValues)
                    }}
                  >
                    <Trash2Icon className="size-4" />
                  </Button>
                </TooltipWrapper>
              </div>
            ))}

            <TooltipWrapper content={tc('add', { cfl: true })}>
              <Button
                variant="secondary"
                className="w-full"
                onClick={() => {
                  if (newEnumValue === NULL_AS_STRING) return
                  const newValues = [...enumValues, '']
                  handleChange(newValues)
                  setNewEnumValue('')
                }}
              >
                <PlusIcon className="size-4" /> {tc('add', { cfl: true })}
              </Button>
            </TooltipWrapper>
          </div>
        </FormControl>
        <p className="text-sm font-medium text-red-700 dark:text-red-900">
          {formApi.formState.errors.enumValues?.message &&
            t(formApi.formState.errors.enumValues.message)}
        </p>
      </FormItem>
    )
  }

const renderDefaultValueField =
  (formApi: FormApi, enumValues: string[]) =>
  ({ field }: { field: ControllerRenderProps<MultiselectFormValues, 'defaultValue'> }) => {
    const { tc } = useT()

    const handleChange = (value: string) => {
      formApi.clearErrors('defaultValue')
      formApi.setValue('defaultValue', value, {
        shouldDirty: true,
        shouldValidate: true,
      })
    }

    useEffect(() => {
      const currentDefaultValue = field.value
      if (currentDefaultValue !== NULL_AS_STRING && !enumValues.includes(currentDefaultValue)) {
        const newDefaultValue = enumValues.length > 0 ? enumValues[0] : ''
        if (!newDefaultValue) return
        handleChange(newDefaultValue)
      }
    }, [enumValues])

    const handleSelection = (selectedItemIds: string[]) => {
      const values = enumValues.filter((_, index) => selectedItemIds.includes(index.toString()))
      if (values.length === 0) {
        handleChange(NULL_AS_STRING)
      } else {
        handleChange(values.join(','))
      }
    }
    const initialSelectedIds = field.value.includes(NULL_AS_STRING)
      ? []
      : field.value
          .split(', ')
          .map((v) => {
            const index = enumValues.indexOf(v)
            return index !== -1 ? index.toString() : ''
          })
          .filter((v) => v !== '')
    return (
      <FormItem>
        <FormLabel>
          {tc('defaultValue', { cfl: true })} ({tc('optional')})
        </FormLabel>
        <FormControl>
          <div className="flex items-center gap-2">
            <MultiSelect
              className="w-full border-primary-200 rounded-lg border p-4"
              items={enumValues
                .filter((v) => v.trim() !== '')
                .map((v, index) => ({
                  id: index.toString(),
                  label: v,
                }))}
              initialSelectedIds={initialSelectedIds}
              onSelection={handleSelection}
            />
            <TooltipWrapper content={tc('delete', { cfl: true })}>
              <Button
                variant="outline"
                onClick={() => handleChange(NULL_AS_STRING)}
                size="icon"
                disabled={field.value === NULL_AS_STRING}
              >
                <Trash2Icon className="size-4" />
              </Button>
            </TooltipWrapper>
          </div>
        </FormControl>
        <FormMessage />
      </FormItem>
    )
  }
